// SearchFieldBar — единая панель живого поиска с «флажками» полей (R63, U-34-D).
// Контракт: поле q (debounce 300мс) + кнопка «Поля» (Popover с чекбоксами выбранных полей)
// + чипы выбранных флажков + «Сбросить». Компонент controlled: value {q, fields} → onChange.
// Поля в запрос уходят CSV (`fields`), отсутствие флажков = поиск по всем полям (SSOT §3.3).

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  InputAdornment,
  Popover,
  Stack,
  TextField,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import { debounce } from '@/utils/performance';
import { t } from '@/i18n';

/** Опция поля поиска: ключ флажка (wire-значение `fields`) и человеческая подпись. */
export interface SearchFieldOption {
  key: string;
  label: string;
}

/** Значение панели: строка поиска и выбранные поля. */
export interface SearchFieldValue {
  q: string;
  fields: string[];
}

/** Задержка debounce живого поиска (канон R33c: 300 мс). */
const SEARCH_DEBOUNCE_MS = 300;

/** Ширина поля ввода поиска (px). */
const SEARCH_INPUT_WIDTH = 320;

interface SearchFieldBarProps {
  /** Справочник полей для чекбоксов «Панели полей» (SSOT-перечень сущности). */
  fieldsOptions: SearchFieldOption[];
  /** Текущее значение (контролируемый компонент). */
  value: SearchFieldValue;
  /** Изменение значения: q — после debounce, fields — сразу. */
  onChange: (value: SearchFieldValue) => void;
  /** Плейсхолдер поля ввода. */
  placeholder?: string;
  /** ARIA-label поля ввода (по умолчанию `common.search`). */
  searchAriaLabel?: string;
}

/**
 * Панель поиска с флажками: `value.fields` → CSV в `fields`, `value.q` — после
 * дебаунса. «Сбросить» очищает и q, и поля (kiss: один клик).
 */
export function SearchFieldBar({
  fieldsOptions,
  value,
  onChange,
  placeholder,
  searchAriaLabel,
}: SearchFieldBarProps): JSX.Element {
  const [input, setInput] = useState(value.q);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Обновлённые ref'ы: debounce не пересоздаётся на каждый рендер (терять pending-таймер нельзя).
  const onChangeRef = useRef(onChange);
  const fieldsRef = useRef(value.fields);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  useEffect(() => {
    fieldsRef.current = value.fields;
  }, [value.fields]);

  // Синхронизация поля с внешним сбросом (value.q меняется извне — «Сбросить» у родителя).
  useEffect(() => {
    setInput(value.q);
  }, [value.q]);

  const applySearch = useMemo(
    () =>
      debounce((nextQ: string) => {
        onChangeRef.current({ q: nextQ, fields: fieldsRef.current });
      }, SEARCH_DEBOUNCE_MS),
    [],
  );

  /** Переключает флажок: изменение fields отдаётся сразу (без debounce). */
  const toggleField = (key: string): void => {
    const next = value.fields.includes(key)
      ? value.fields.filter((f) => f !== key)
      : [...value.fields, key];
    onChangeRef.current({ q: input, fields: next });
  };

  /** Полный сброс: q пуст, флажки сняты. */
  const handleReset = (): void => {
    setInput('');
    onChangeRef.current({ q: '', fields: [] });
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      <TextField
        size="small"
        {...(placeholder !== undefined ? { placeholder } : {})}
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          applySearch(e.target.value);
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ width: SEARCH_INPUT_WIDTH }}
        inputProps={{ 'aria-label': searchAriaLabel ?? t('common.search') }}
      />
      <Button
        variant={value.fields.length > 0 ? 'contained' : 'outlined'}
        size="small"
        startIcon={<TuneIcon />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-label={t('search.fields.label')}
        data-testid="search-fields-trigger"
      >
        {t('search.fields.label')}
        {value.fields.length > 0 ? ` (${value.fields.length})` : ''}
      </Button>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        data-testid="search-fields-panel"
      >
        <Stack sx={{ p: 2, minWidth: 240 }} spacing={0}>
          {fieldsOptions.map((option) => (
            <FormControlLabel
              key={option.key}
              control={
                <Checkbox
                  size="small"
                  checked={value.fields.includes(option.key)}
                  onChange={() => toggleField(option.key)}
                />
              }
              label={option.label}
            />
          ))}
        </Stack>
      </Popover>
      {value.fields.map((key) => {
        const option = fieldsOptions.find((o) => o.key === key);
        if (!option) return null;
        return <Chip key={key} label={option.label} size="small" onDelete={() => toggleField(key)} />;
      })}
      <Button variant="text" size="small" onClick={handleReset}>
        {t('search.reset')}
      </Button>
    </Stack>
  );
}
